import { format, useT } from "@/i18n";
import type { ReactNode } from "react";
import { HiCheckCircle, HiOutlineCloud, HiPause, HiVolumeUp, HiXCircle } from "react-icons/hi";
import { COLORS, rs } from "./readOnlyStyles";
import { getScoreColor } from "./scoring";

/** Shell every read-only template shares: frozen prompt on top, the student's work below. */
export function ReadOnlyCard({
  title,
  score,
  children,
}: {
  title: string;
  score: number | null;
  children: ReactNode;
}) {
  return (
    <div style={rs.card}>
      <div style={rs.headerRow}>
        <h2 style={rs.title}>{title}</h2>
        <ScorePill score={score} />
      </div>
      <div style={rs.bodyContent}>{children}</div>
    </div>
  );
}

export function ScorePill({ score }: { score: number | null }) {
  const t = useT();
  const color = score === null ? COLORS.textTertiary : getScoreColor(score);

  return (
    <span style={{ ...rs.scorePill, ...rs.scorePillText, borderColor: color, color }}>
      {score === null ? t["readonly.notScored"] : Math.round(score)}
    </span>
  );
}

export function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section style={rs.section}>
      <p style={rs.sectionLabel}>{label}</p>
      {children}
    </section>
  );
}

/** One graded thing the student did — their answer, and the expected one when they missed it. */
export function AnswerLine({
  label,
  studentAnswer,
  correctAnswer,
  isCorrect,
  score,
  accessory,
}: {
  label?: string | undefined;
  studentAnswer: string;
  correctAnswer?: string | null | undefined;
  isCorrect?: boolean | undefined;
  score?: number | null | undefined;
  /** Sits at the end of the answer row — where a template puts the student's speaker. */
  accessory?: ReactNode;
}) {
  const t = useT();
  const passed = isCorrect ?? (typeof score === "number" ? score >= 70 : undefined);
  const answerColor = passed === undefined ? COLORS.text : passed ? COLORS.success : COLORS.warning;
  const showCorrect = passed === false && !!correctAnswer && correctAnswer !== studentAnswer;

  return (
    <div style={rs.answerLine}>
      {label ? <p style={rs.answerLabel}>{label}</p> : null}
      <div style={rs.answerRow}>
        <span style={{ ...rs.answerText, color: answerColor }}>{studentAnswer || "—"}</span>
        {typeof score === "number" && (
          <span style={{ ...rs.answerScore, color: getScoreColor(score) }}>
            {Math.round(score)}
          </span>
        )}
        {accessory}
      </div>
      {showCorrect && (
        <p style={rs.correctText}>{format(t["readonly.correct"], { answer: correctAnswer })}</p>
      )}
    </div>
  );
}

/** Answers are compared the same loose way the live slides graded them. */
const normalize = (value: string) => value.trim().toLowerCase();

/**
 * The choices as the student saw them, with the right one green and a wrong pick red. Snapshots
 * store only the distractors, so the correct answer is added back the way the live slide does.
 */
export function OptionReview({
  options,
  correctAnswer,
  studentAnswer,
}: {
  options: readonly string[];
  correctAnswer?: string | null | undefined;
  studentAnswer: string;
}) {
  const t = useT();
  const allOptions = correctAnswer
    ? [correctAnswer, ...options.filter((option) => normalize(option) !== normalize(correctAnswer))]
    : options;

  return (
    <>
      {allOptions.map((option) => {
        const isCorrect = normalize(option) === normalize(correctAnswer ?? "");
        const isPicked = normalize(option) === normalize(studentAnswer);

        return (
          <div
            key={option}
            style={{
              ...rs.option,
              ...(isCorrect && rs.optionCorrect),
              ...(isPicked && !isCorrect && rs.optionWrong),
            }}
          >
            <span
              style={{
                ...rs.optionText,
                ...(isCorrect && rs.optionTextCorrect),
                ...(isPicked && !isCorrect && rs.optionTextWrong),
              }}
            >
              {option}
            </span>
            {isPicked && <span style={rs.pickedTag}>{t["readonly.yourAnswer"]}</span>}
            {isCorrect && <HiCheckCircle size={18} color="#22c55e" />}
            {isPicked && !isCorrect && <HiXCircle size={18} color="#ef4444" />}
          </div>
        );
      })}
    </>
  );
}

/**
 * Play control for one stored clip: an icon that sits beside whatever it plays, so the card never
 * explains in words which clip is which. Colour is the only cue for whose voice it is — lavender
 * for the prompt, yellow for the student — matching the conversation bubbles. Greyed out, rather
 * than hidden, once a recording is gone, so the reader sees there was one.
 *
 * `label` names the clip for screen readers only ("my recording"), and is prefixed with Play/Stop.
 */
export function SpeakerButton({
  label,
  url,
  isPlaying,
  onPress,
  variant = "prompt",
}: {
  label: string;
  url: string | null;
  isPlaying: boolean;
  onPress: () => void;
  variant?: "prompt" | "student";
}) {
  const t = useT();
  const isStudent = variant === "student";
  const Glyph = isPlaying ? HiPause : HiVolumeUp;

  return (
    <button
      type="button"
      disabled={!url}
      onClick={onPress}
      aria-label={format(
        url
          ? isPlaying
            ? t["readonly.stopClip"]
            : t["readonly.playClip"]
          : t["readonly.clipUnavailable"],
        { label }
      )}
      style={{
        ...rs.speaker,
        ...(isStudent ? rs.speakerStudent : rs.speakerPrompt),
        ...(!url && rs.speakerDisabled),
      }}
    >
      <Glyph size={18} />
    </button>
  );
}

/** One piece of prompt media, as a snapshot stores it: a signed URL, or an id with no key left. */
export interface PromptMedia {
  src?: string | null | undefined;
  /** Signed URL of the compressed WebP variant, added per request from the image record. */
  displaySrc?: string | null | undefined;
  id?: string | null | undefined;
}

/**
 * Renders the prompt's image, or names it when the play predates snapshots keeping the S3 key.
 * Nothing at all when the slide never had one. SVG needs no special case here — unlike RN's
 * <Image>, an <img> draws vector art itself.
 */
export function PromptImage({
  media,
  label,
}: { media?: PromptMedia | null | undefined; label: string }) {
  const src = media?.displaySrc ?? media?.src;
  if (src) {
    return <img src={src} alt={label} style={rs.promptImage} />;
  }
  return media?.id ? <MissingMedia label={label} /> : null;
}

/** Paused on mount — a review page should never start playing on its own. */
export function PromptVideo({
  media,
  label,
}: { media?: PromptMedia | null | undefined; label: string }) {
  if (media?.src) {
    return (
      <video src={media.src} controls preload="metadata" style={rs.promptVideo}>
        <track kind="captions" />
      </video>
    );
  }
  return media?.id ? <MissingMedia label={label} /> : null;
}

/** The clip the slide played to the student. Nothing at all when the slide never had one. */
export function PromptAudio({
  media,
  label,
  isPlaying,
  onPress,
}: {
  media?: PromptMedia | null | undefined;
  label: string;
  isPlaying: boolean;
  onPress: () => void;
}) {
  if (!media?.src && !media?.id) return null;
  return (
    <SpeakerButton label={label} url={media.src ?? null} isPlaying={isPlaying} onPress={onPress} />
  );
}

/** Named but not shown: this play was recorded before snapshots kept the media's storage key. */
export function MissingMedia({ label }: { label: string }) {
  return (
    <div style={rs.missingMedia}>
      <HiOutlineCloud size={16} color="#AFAFAF" />
      <span style={rs.missingMediaText}>{label}</span>
    </div>
  );
}

/** A block of coaching prose — on its own accented tint so it reads as advice, not a muted aside. */
export function FeedbackBox({ label, text }: { label?: string; text: string }) {
  const t = useT();

  return (
    <div style={rs.feedbackBox}>
      <p style={rs.feedbackLabel}>{label ?? t["readonly.feedback"]}</p>
      <p style={rs.feedbackText}>{text}</p>
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return <p style={rs.emptyText}>{message}</p>;
}

/** The tinted block a template leads with — prompt art, the word, the instruction. */
export function Hero({ children }: { children: ReactNode }) {
  return <div style={rs.hero}>{children}</div>;
}
